import React, { useState, useEffect } from 'react';
import { ShoppingCart, Loader2, ChevronDown, Truck, Package } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = ['pending_payment', 'paid', 'processing', 'shipped', 'delivered', 'cancelled', 'refunded'];
const STATUS_COLORS = {
  pending_payment: 'bg-amber-100 text-amber-700',
  paid: 'bg-blue-100 text-blue-700',
  processing: 'bg-purple-100 text-purple-700',
  shipped: 'bg-indigo-100 text-indigo-700',
  delivered: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
  refunded: 'bg-gray-100 text-gray-700',
};

const AdminShopOrders = () => {
  const { toast } = useToast();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [updatingOrder, setUpdatingOrder] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => { loadOrders(); }, [filterStatus]);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const params = filterStatus ? `?status=${filterStatus}` : '';
      const res = await fetch(`${API_URL}/api/shop/orders${params}`, { headers });
      const data = await res.json();
      setOrders(Array.isArray(data) ? data : []);
    } catch { } finally { setLoading(false); }
  };

  const updateOrder = async (orderId, updates) => {
    setUpdatingOrder(orderId);
    try {
      const res = await fetch(`${API_URL}/api/shop/orders/${orderId}`, { method: 'PUT', headers, body: JSON.stringify(updates) });
      if (!res.ok) throw new Error((await res.json()).detail);
      toast({ title: 'Order updated' });
      loadOrders();
    } catch (err) {
      toast({ title: 'Update failed', description: err.message, variant: 'destructive' });
    } finally { setUpdatingOrder(null); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Orders</h1>
          <p className="text-gray-500 text-sm">{orders.length} orders</p>
        </div>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300" data-testid="order-status-filter">
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-pink-500" /></div>
      ) : orders.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center shadow-sm">
          <ShoppingCart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No orders found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((order) => (
            <div key={order.id} className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50" onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}>
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">{order.user_name || order.user_email}</p>
                    <p className="text-xs text-gray-500">{new Date(order.created_at).toLocaleDateString()} • {order.items?.length || 0} item(s)</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-bold text-gray-900 text-sm">{order.currency_symbol || 'CA$'}{parseFloat(order.total).toFixed(2)}</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[order.status] || 'bg-gray-100 text-gray-600'}`}>
                    {order.status?.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${expandedOrder === order.id ? 'rotate-180' : ''}`} />
                </div>
              </div>

              {expandedOrder === order.id && (
                <div className="border-t border-gray-100 p-4">
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Items */}
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3 text-sm">Order Items</h4>
                      <div className="space-y-2">
                        {order.items?.map((item, i) => (
                          <div key={i} className="flex items-center gap-3 text-sm">
                            <div className="w-10 h-10 rounded-lg bg-gray-100 flex-shrink-0 overflow-hidden">
                              {item.image ? <img src={item.image} alt="" className="w-full h-full object-cover" /> : <Package className="w-4 h-4 text-gray-400 m-auto mt-3" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900 truncate">{item.name}</p>
                              <p className="text-gray-500 text-xs">Qty: {item.quantity} × {order.currency_symbol}{parseFloat(item.price).toFixed(2)}</p>
                            </div>
                            <span className="font-semibold text-gray-900">{order.currency_symbol}{parseFloat(item.line_total).toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                      <div className="mt-3 pt-3 border-t border-gray-100 space-y-1 text-xs text-gray-600">
                        <div className="flex justify-between"><span>Subtotal</span><span>{order.currency_symbol}{parseFloat(order.subtotal).toFixed(2)}</span></div>
                        <div className="flex justify-between"><span>Tax</span><span>{order.currency_symbol}{parseFloat(order.tax).toFixed(2)}</span></div>
                        <div className="flex justify-between"><span>Shipping</span><span>{order.shipping === 0 ? 'Free' : `${order.currency_symbol}${parseFloat(order.shipping).toFixed(2)}`}</span></div>
                        <div className="flex justify-between font-bold text-gray-900 text-sm pt-1 border-t"><span>Total</span><span>{order.currency_symbol}{parseFloat(order.total).toFixed(2)}</span></div>
                      </div>
                    </div>

                    {/* Shipping & Admin Controls */}
                    <div>
                      {order.shipping_info && (
                        <div className="mb-4">
                          <h4 className="font-semibold text-gray-900 mb-2 text-sm">Ship To</h4>
                          <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600 space-y-1">
                            <p className="font-medium text-gray-900">{order.shipping_info.full_name}</p>
                            <p>{order.shipping_info.email}</p>
                            <p>{order.shipping_info.phone}</p>
                            <p>{order.shipping_info.address}</p>
                            <p>{[order.shipping_info.city, order.shipping_info.province, order.shipping_info.postal_code].filter(Boolean).join(', ')}</p>
                          </div>
                        </div>
                      )}

                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Update Status</label>
                          <select
                            defaultValue={order.status}
                            onChange={(e) => updateOrder(order.id, { status: e.target.value })}
                            disabled={updatingOrder === order.id}
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
                            data-testid={`order-status-${order.id}`}
                          >
                            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Tracking Number</label>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              defaultValue={order.tracking_number || ''}
                              onBlur={(e) => e.target.value !== (order.tracking_number || '') && updateOrder(order.id, { tracking_number: e.target.value })}
                              className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
                              placeholder="Enter tracking number"
                              data-testid={`tracking-${order.id}`}
                            />
                            <button className="p-2 bg-indigo-100 text-indigo-600 rounded-lg hover:bg-indigo-200 transition-colors">
                              <Truck className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminShopOrders;
